import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Use a relative path so Nginx proxies it to your Express server over HTTPS
const API_BASE = '/api';

const StoreGrid = ({ storeName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageDebug, setImageDebug] = useState({});

  const navigate = useNavigate();
  const { city } = useParams();

  // Helper function to process image URLs
  const processImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    console.log(`Processing image URL: "${imageUrl}"`);
    
    // Handle different URL patterns
    if (imageUrl.startsWith('/uploads')) {
      const newUrl = `${API_BASE}${imageUrl}`;
      console.log(`Image starts with '/uploads', transformed to: "${newUrl}"`);
      return newUrl;
    } else if (imageUrl.startsWith('http://')) {
      // Convert HTTP to HTTPS if site is served over HTTPS
      if (window.location.protocol === 'https:') {
        const newUrl = imageUrl.replace('http://', 'https://');
        console.log(`Converting HTTP to HTTPS: "${newUrl}"`);
        return newUrl;
      }
      console.log(`URL is already absolute (HTTP): "${imageUrl}"`);
      return imageUrl;
    } else if (imageUrl.startsWith('https://')) {
      console.log(`URL is already absolute (HTTPS): "${imageUrl}"`);
      return imageUrl;
    } else if (imageUrl.startsWith('/api/')) {
      console.log(`URL already has API prefix: "${imageUrl}"`);
      return imageUrl;
    } else {
      // For any other format, prepend API_BASE
      const newUrl = `${API_BASE}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
      console.log(`Other URL format, transformed to: "${newUrl}"`);
      return newUrl;
    }
  };

  useEffect(() => {
    console.log(`StoreGrid: Fetching products for store "${storeName}" from ${API_BASE}/stores/${storeName}/products`);
    
    // Fetch products using the relative API URL
    fetch(`${API_BASE}/stores/${storeName}/products`)
      .then(response => {
        console.log(`API response status: ${response.status}`);
        if (!response.ok) {
          throw new Error(`Error loading products (Status: ${response.status})`);
        }
        return response.json();
      })
      .then(data => {
        console.log(`Received ${data.length} products from API`);
        
        // Log detailed product information for debugging
        const productLog = data.map(p => ({
          id: p.id,
          name: p.name,
          originalImageUrl: p.image_url,
          processedImageUrl: processImageUrl(p.image_url),
          price: p.price
        }));
        
        console.log('Products with image URLs:', productLog);
        
        // Store image debug info
        const debugInfo = {};
        productLog.forEach(p => {
          debugInfo[p.id] = {
            original: p.originalImageUrl,
            processed: p.processedImageUrl,
            loaded: false,
            error: false
          };
        });
        setImageDebug(debugInfo);
        
        setProducts(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        setError(`Error loading products: ${err.message}`);
        setLoading(false);
      });
  }, [storeName]);

  const handleProductClick = (productId) => {
    navigate(`/${city}/${storeName}/product/${productId}`);
  };

  // Handle image load success
  const handleImageLoad = (productId) => {
    console.log(`✅ Image loaded successfully for product ID: ${productId}`);
    setImageDebug(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        loaded: true
      }
    }));
  };

  // Handle image load error
  const handleImageError = (productId, originalUrl, event) => {
    console.error(`❌ Failed to load image for product ID: ${productId}, URL: ${event.target.src}`);
    
    // Store error information
    setImageDebug(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        error: true,
        errorUrl: event.target.src
      }
    }));
    
    // Try alternative URL format as fallback
    const originalSrc = event.target.src;
    
    // Only try fallback if not already tried
    if (!originalSrc.includes('fallback')) {
      // Extract filename and build alternate path
      const filename = originalUrl.split('/').pop();
      const fallbackUrl = `${API_BASE}/uploads/${filename}`;
      
      console.log(`Trying fallback URL: ${fallbackUrl}`);
      event.target.src = fallbackUrl;
      event.target.dataset.fallback = 'true';
    } else {
      // If fallback also failed, show placeholder
      console.log(`Fallback also failed, showing placeholder for product ID: ${productId}`);
    }
  };

  if (loading) {
    return <div className="container text-center py-5">Loading...</div>;
  }
  if (error) {
    return <div className="container text-center py-5 text-danger">{error}</div>;
  }

  return (
    <div className="container px-4">
      {/* Debug panel - only shown in development */}
      {process.env.NODE_ENV !== 'production' && (
        <div className="mb-4 p-3 border rounded" style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }}>
          <h5>Product Image Debug</h5>
          <p>Total products: {products.length}</p>
          <div style={{ maxHeight: '200px', overflow: 'auto' }}>
            <table className="table table-sm" style={{ fontSize: '0.75rem' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Original URL</th>
                  <th>Processed URL</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product.id}>
                    <td>{product.id}</td>
                    <td>{product.name}</td>
                    <td style={{ wordBreak: 'break-all' }}>{product.image_url}</td>
                    <td style={{ wordBreak: 'break-all' }}>
                      {processImageUrl(product.image_url)}
                    </td>
                    <td>
                      {imageDebug[product.id]?.loaded ? 
                        '✅ Loaded' : 
                        imageDebug[product.id]?.error ? 
                          '❌ Failed' : 
                          '⏳ Unknown'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button 
            className="btn btn-sm btn-secondary mt-2"
            onClick={() => {
              console.log('Full product data:', products);
              console.log('Image debug info:', imageDebug);
            }}
          >
            Log Full Data to Console
          </button>
        </div>
      )}

      <div className="row g-0">
        {products.map((product) => (
          <div
            key={product.id}
            className="col-4 p-1"
            onClick={() => handleProductClick(product.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="position-relative instagram-card">
              <div className="aspect-ratio-container">
                <img
                  src={processImageUrl(product.image_url)}
                  alt={product.name}
                  className="img-fluid w-100 h-100 object-fit-cover"
                  onLoad={() => handleImageLoad(product.id)}
                  onError={(e) => handleImageError(product.id, product.image_url, e)}
                />
                <div className="hover-overlay">
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-price">${Number(product.price).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* No products message */}
      {products.length === 0 && !loading && !error && (
        <div className="text-center py-5">
          <p className="mb-0">No products available at this time.</p>
        </div>
      )}
    </div>
  );
};

export default StoreGrid;